import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calendar, Download, FileArchive, FileText, Loader2, TrendingUp, TrendingDown, FileSignature, Archive, Trash2, Music } from "lucide-react";
import { fetchMonthData, buildMergedPdf, buildMonthZip, triggerDownload, saveMonthClosing, fetchMonthClosings, downloadStoredClosing, deleteMonthClosing } from "../lib/monatsabschlussEngine";
import { renderUstvaPdf, buildUstvaCsv, computeUstva } from "../lib/ustvaEngine";

function formatEUR(v) {
  return (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function buildMonthOptions() {
  // Letzte 24 Monate + aktueller Monat
  const now = new Date();
  const opts = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
    opts.push({ key, label });
  }
  return opts;
}

export default function DriveMonatsabschluss() {
  const months = useMemo(() => buildMonthOptions(), []);
  const [month, setMonth] = useState(months[0].key);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busyKind, setBusyKind] = useState(null); // "zip" | "pdf" | "archive"
  const [progress, setProgress] = useState("");
  const [archive, setArchive] = useState([]);
  const [archiveBusy, setArchiveBusy] = useState(null);

  const loadArchive = async () => {
    try {
      const rows = await fetchMonthClosings();
      setArchive(rows);
    } catch (e) {
      console.warn("Archiv laden fehlgeschlagen:", e);
    }
  };

  useEffect(() => { loadArchive(); }, []);

  const load = async (m) => {
    setLoading(true);
    try {
      const d = await fetchMonthData(m);
      setData(d);
    } catch (e) {
      toast.error("Fehler beim Laden: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

  const downloadPdf = async () => {
    if (!data) return;
    setBusyKind("pdf");
    setProgress("Starte…");
    try {
      const blob = await buildMergedPdf(month, data, (msg) => setProgress(msg));
      triggerDownload(blob, `Monatsabschluss-${month}-KOMPLETT.pdf`);
      toast.success("Komplett-PDF heruntergeladen");
    } catch (e) {
      toast.error("Fehler: " + e.message);
    } finally {
      setBusyKind(null);
      setProgress("");
    }
  };

  const downloadUstva = async () => {
    if (!data) return;
    setBusyKind("ustva");
    try {
      const pdf = await renderUstvaPdf(month, data);
      triggerDownload(pdf.output("blob"), `UStVA-${month}.pdf`);
      toast.success("UStVA-Export heruntergeladen");
    } catch (e) { toast.error("Fehler: " + e.message); }
    finally { setBusyKind(null); }
  };

  const downloadUstvaCsv = () => {
    if (!data) return;
    try {
      const csv = buildUstvaCsv(month, data);
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      triggerDownload(blob, `UStVA-${month}.csv`);
    } catch (e) { toast.error("Fehler: " + e.message); }
  };

  const downloadEuer = async () => {
    if (!data) return;
    setBusyKind("euer");
    try {
      // Lade Jahresdaten
      const year = month.slice(0, 4);
      const { renderEuerPdf } = await import("../lib/ustvaEngine");
      const pdf = await renderEuerPdf(year);
      triggerDownload(pdf.output("blob"), `EUER-${year}.pdf`);
      toast.success("EÜR-Jahresübersicht heruntergeladen");
    } catch (e) { toast.error("Fehler: " + e.message); }
    finally { setBusyKind(null); }
  };

  const downloadZip = async () => {
    if (!data) return;
    setBusyKind("zip");
    setProgress("Starte…");
    try {
      const blob = await buildMonthZip(month, data, (msg) => setProgress(msg));
      triggerDownload(blob, `Monatsabschluss-${month}.zip`);
      toast.success("ZIP heruntergeladen");
    } catch (e) {
      toast.error("Fehler: " + e.message);
    } finally {
      setBusyKind(null);
      setProgress("");
    }
  };

  const archiveClosing = async () => {
    if (!data) return;
    if (totalCount === 0) { toast.error("Keine Daten in diesem Monat"); return; }
    setBusyKind("archive");
    setProgress("Starte…");
    try {
      await saveMonthClosing(month, data, (msg) => setProgress(msg));
      toast.success(`Monatsabschluss ${monthLabel} archiviert`);
      await loadArchive();
    } catch (e) {
      toast.error("Fehler: " + e.message);
    } finally {
      setBusyKind(null);
      setProgress("");
    }
  };

  const downloadFromArchive = async (row, kind) => {
    setArchiveBusy(`${row.id}-${kind}`);
    try {
      const path = kind === "zip" ? row.zip_storage_path : row.pdf_storage_path;
      const fname = kind === "zip" ? `Monatsabschluss-${row.month}.zip` : `Uebersicht-${row.month}.pdf`;
      await downloadStoredClosing(path, fname);
    } catch (e) {
      toast.error("Fehler: " + e.message);
    } finally {
      setArchiveBusy(null);
    }
  };

  const removeFromArchive = async (row) => {
    if (!window.confirm(`Monatsabschluss ${row.month} wirklich aus dem Archiv löschen?`)) return;
    setArchiveBusy(`${row.id}-del`);
    try {
      await deleteMonthClosing(row);
      toast.success("Aus dem Archiv gelöscht");
      await loadArchive();
    } catch (e) {
      toast.error("Fehler: " + e.message);
    } finally {
      setArchiveBusy(null);
    }
  };

  const monthLabel = months.find((m) => m.key === month)?.label || month;
  const totalCount = (data?.totals.outgoing.count || 0) + (data?.totals.incoming.count || 0) + (data?.totals.contractCount || 0) + (data?.totals.musichub?.count || 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-3">Monatsabschluss</div>
        <div className="font-display font-black uppercase text-3xl md:text-4xl mb-2" data-testid="monatsabschluss-title">
          Buchhaltung & ELSTER-Export
        </div>
        <div className="text-sm text-zinc-400 max-w-2xl">
          Alle Rechnungen, MusicHub-Abrechnungen, Verträge und Provisionen eines Monats gebündelt. Enthält den <strong className="text-white">UStVA-Export</strong> mit allen Zahlen, die du in ELSTER eintragen musst.
        </div>
      </div>

      {/* Month selector */}
      <div className="bg-[#0A0A0A] border border-white/[0.06] p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Calendar size={16} className="text-[#ff0033]" />
          <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Monat auswählen</label>
        </div>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="month-selector"
          className="w-full md:w-80 bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60"
        >
          {months.map((m) => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Preview */}
      {loading && (
        <div className="flex items-center gap-3 text-zinc-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Lade Daten für {monthLabel}…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard
              icon={<TrendingUp size={16} className="text-emerald-400" />}
              label="Ausgangsrechnungen"
              value={`${data.totals.outgoing.count}`}
              sub={formatEUR(data.totals.outgoing.total)}
              testid="stat-outgoing"
            />
            <StatCard
              icon={<TrendingDown size={16} className="text-orange-400" />}
              label="Eingangsrechnungen"
              value={`${data.totals.incoming.count}`}
              sub={formatEUR(data.totals.incoming.total)}
              testid="stat-incoming"
            />
            <StatCard
              icon={<Music size={16} className="text-blue-400" />}
              label="MusicHub"
              value={`${data.totals.musichub?.count || 0}`}
              sub={formatEUR(data.totals.musichub?.total || 0)}
              testid="stat-musichub"
            />
            <StatCard
              icon={<FileSignature size={16} className="text-[#ff0033]" />}
              label="Verträge"
              value={`${data.totals.contractCount}`}
              sub="in diesem Monat"
              testid="stat-contracts"
            />
            <StatCard
              icon={<TrendingUp size={16} className="text-white" />}
              label="Netto"
              value={formatEUR(data.totals.net)}
              sub="Ausgang+MH − Eingang"
              accent
              testid="stat-net"
            />
          </div>

          {/* Preview Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PreviewList
              title="Ausgangsrechnungen"
              rows={data.outgoing.map((r) => ({
                left: r.invoice_number,
                middle: r.drive_artists?.artist_name || r.drive_customers?.name || r.drive_externals?.full_name || "—",
                right: formatEUR(r.total),
                date: r.invoice_date,
              }))}
              testid="list-outgoing"
            />
            <PreviewList
              title="Eingangsrechnungen"
              rows={data.incoming.map((r) => ({
                left: r.invoice_number,
                middle: r.drive_artists?.artist_name || r.drive_customers?.name || r.drive_externals?.full_name || r.recipient_snapshot?.name || "—",
                right: formatEUR(r.total),
                date: r.invoice_date,
              }))}
              testid="list-incoming"
            />
            <PreviewList
              title="MusicHub-Abrechnungen"
              rows={(data.musichub || []).map((r) => ({
                left: r.title || r.file_name,
                middle: r.notes || "—",
                right: formatEUR(r.amount),
                date: r.created_at,
              }))}
              testid="list-musichub"
            />
            <PreviewList
              title="Verträge"
              rows={data.contracts.map((r) => ({
                left: r.title,
                middle: r.drive_artists?.artist_name || r.drive_customers?.name || r.drive_externals?.full_name || "—",
                right: (r.status || "draft").toUpperCase(),
                date: r.created_at,
              }))}
              testid="list-contracts"
              wide
            />
          </div>

          {/* Actions */}
          <div className="border-t border-white/[0.06] pt-6 flex flex-wrap items-center gap-4">
            <button
              onClick={downloadPdf}
              disabled={busyKind !== null || totalCount === 0}
              data-testid="download-pdf"
              className="group relative inline-flex items-center gap-3 bg-white text-black px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_28px_rgba(255,0,51,0.55)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busyKind === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              <span className="relative z-10 group-hover:text-white transition-colors">Komplett-PDF</span>
              <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            </button>
            <button
              onClick={downloadZip}
              disabled={busyKind !== null || totalCount === 0}
              data-testid="download-zip"
              className="inline-flex items-center gap-3 bg-[#ff0033] text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_28px_rgba(255,0,51,0.55)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busyKind === "zip" ? <Loader2 size={14} className="animate-spin" /> : <FileArchive size={14} />}
              <span>Komplettes ZIP</span>
            </button>
            <button
              onClick={archiveClosing}
              disabled={busyKind !== null || totalCount === 0}
              data-testid="archive-closing"
              className="inline-flex items-center gap-3 bg-transparent border border-white/20 text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:border-[#ff0033]/60 hover:text-[#ff0033] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busyKind === "archive" ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
              <span>Im Archiv speichern</span>
            </button>
            {(busyKind === "zip" || busyKind === "archive") && progress && (
              <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{progress}</span>
            )}
            {totalCount === 0 && (
              <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Keine Daten für diesen Monat</span>
            )}
          </div>

          <div className="text-xs text-zinc-500 leading-relaxed max-w-3xl">
            <strong className="text-zinc-300">Hinweis:</strong> Das <strong className="text-white">Komplett-PDF</strong> enthält die Übersicht als Deckblatt und darunter alle Original-Rechnungen (Ausgang/Eingang), MusicHub-Abrechnungen und Verträge – zusammengeführt zu einem einzigen Dokument. Das <strong className="text-white">ZIP</strong> enthält zusätzlich alle Dateien in Unterordnern. Mit „Im Archiv speichern" wird beides dauerhaft hinterlegt.
          </div>

          {/* ELSTER-Sektion */}
          <div className="pt-8 mt-4 border-t border-white/[0.06]">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-2">ELSTER-Export</div>
            <div className="font-display font-black uppercase text-xl md:text-2xl mb-4">Steuerliche Übersichten</div>

            {/* UStVA-Preview Box */}
            <UstvaPreview data={data} />

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button
                onClick={downloadUstva}
                disabled={busyKind !== null || totalCount === 0}
                data-testid="download-ustva"
                className="inline-flex items-center gap-3 bg-transparent border-2 border-[#ff0033] text-[#ff0033] hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busyKind === "ustva" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                <span>UStVA-PDF ({month})</span>
              </button>
              <button
                onClick={downloadUstvaCsv}
                disabled={busyKind !== null || totalCount === 0}
                data-testid="download-ustva-csv"
                className="inline-flex items-center gap-3 border border-white/15 text-zinc-300 hover:border-[#ff0033]/50 hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase transition-colors disabled:opacity-40"
              >
                <FileText size={14} />
                <span>UStVA-CSV</span>
              </button>
              <button
                onClick={downloadEuer}
                disabled={busyKind !== null}
                data-testid="download-euer"
                className="inline-flex items-center gap-3 border border-white/15 text-zinc-300 hover:border-[#ff0033]/50 hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase transition-colors disabled:opacity-40"
              >
                {busyKind === "euer" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                <span>EÜR-Jahresübersicht ({month.slice(0, 4)})</span>
              </button>
            </div>

            <div className="text-xs text-zinc-500 leading-relaxed max-w-3xl mt-4">
              <strong className="text-zinc-300">UStVA:</strong> Monatliche Umsatzsteuer-Voranmeldung mit den ELSTER-Kennzahlen (81, 86, 66, 83). Trage die Zahlen 1:1 im ELSTER-Formular ein. <br />
              <strong className="text-zinc-300">EÜR:</strong> Kumulierte Jahresübersicht aller Einnahmen und Ausgaben. Vorbereitung für deine jährliche EÜR-Erklärung in ELSTER.
            </div>
          </div>
        </>
      )}

      {/* Archiv */}
      <div className="pt-8 border-t border-white/[0.06]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-1">Archiv</div>
            <div className="font-display font-black uppercase text-xl md:text-2xl" data-testid="archive-title">Gespeicherte Monatsabschlüsse</div>
          </div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{archive.length} Einträge</div>
        </div>

        {archive.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/[0.06] px-6 py-10 text-center text-sm text-zinc-500 italic" data-testid="archive-empty">
            Noch keine archivierten Monatsabschlüsse. Klick oben auf „Im Archiv speichern", um den aktuellen Monat dauerhaft zu hinterlegen.
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]" data-testid="archive-list">
            {archive.map((r) => {
              const [yr, mo] = r.month.split("-");
              const label = new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
              return (
                <div key={r.id} className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-4 items-center px-5 py-4">
                  <div>
                    <div className="font-display font-black uppercase text-base text-white">{label}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
                      Archiviert: {new Date(r.created_at).toLocaleDateString("de-DE")} {new Date(r.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px] tracking-[0.15em] uppercase text-zinc-500">
                    <div><div>Ausgang</div><div className="text-white font-bold text-sm mt-1 tracking-normal">{r.outgoing_count} · {formatEUR(r.outgoing_total)}</div></div>
                    <div><div>Eingang</div><div className="text-white font-bold text-sm mt-1 tracking-normal">{r.incoming_count} · {formatEUR(r.incoming_total)}</div></div>
                    <div><div>Verträge</div><div className="text-white font-bold text-sm mt-1 tracking-normal">{r.contract_count}</div></div>
                    <div><div>Netto</div><div className={`font-bold text-sm mt-1 tracking-normal ${Number(r.net_total) >= 0 ? "text-emerald-400" : "text-[#ff0033]"}`}>{formatEUR(r.net_total)}</div></div>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => downloadFromArchive(r, "pdf")}
                      disabled={archiveBusy !== null}
                      data-testid={`arch-pdf-${r.id}`}
                      title="Übersichts-PDF herunterladen"
                      className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-3 py-2 text-[9px] font-bold tracking-[0.25em] uppercase transition-colors disabled:opacity-40"
                    >
                      {archiveBusy === `${r.id}-pdf` ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
                      PDF
                    </button>
                    <button
                      onClick={() => downloadFromArchive(r, "zip")}
                      disabled={archiveBusy !== null}
                      data-testid={`arch-zip-${r.id}`}
                      title="Komplettes ZIP herunterladen"
                      className="inline-flex items-center gap-2 bg-[#ff0033] text-white px-3 py-2 text-[9px] font-bold tracking-[0.25em] uppercase hover:shadow-[0_0_16px_rgba(255,0,51,0.55)] transition-all disabled:opacity-40"
                    >
                      {archiveBusy === `${r.id}-zip` ? <Loader2 size={11} className="animate-spin" /> : <FileArchive size={11} />}
                      ZIP
                    </button>
                    <button
                      onClick={() => removeFromArchive(r)}
                      disabled={archiveBusy !== null}
                      data-testid={`arch-del-${r.id}`}
                      title="Aus dem Archiv löschen"
                      className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-[#ff0033]/50 hover:text-[#ff0033] transition-colors disabled:opacity-40"
                    >
                      {archiveBusy === `${r.id}-del` ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const UstvaPreview = ({ data }) => {
  const u = computeUstva(data);
  const F = (v) => (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] p-5" data-testid="ustva-preview">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-4">UStVA-Vorschau · Kennzahlen für ELSTER</div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_60px_140px] gap-2 text-sm">
        <UstvaRow label="Bemessungsgrundlage 19 %" kz="81" val={F(u.umsatz19)} />
        <UstvaRow label="Umsatzsteuer 19 %" kz="" val={F(u.ust19)} muted />
        {u.umsatz7 > 0 && <>
          <UstvaRow label="Bemessungsgrundlage 7 %" kz="86" val={F(u.umsatz7)} />
          <UstvaRow label="Umsatzsteuer 7 %" kz="" val={F(u.ust7)} muted />
        </>}
        <UstvaRow label="Abziehbare Vorsteuer" kz="66" val={F(u.vorsteuer)} />
        <div className="col-span-full border-t border-white/[0.06] my-2" />
        <UstvaRow label={u.zahllast >= 0 ? "Zahllast (an Finanzamt)" : "Erstattung (vom Finanzamt)"} kz="83" val={F(Math.abs(u.zahllast))} big />
      </div>
    </div>
  );
};

const UstvaRow = ({ label, kz, val, muted, big }) => (
  <>
    <div className={`${muted ? "text-zinc-500" : "text-white"} ${big ? "text-base font-bold" : ""}`}>{label}</div>
    <div className={`${kz ? "text-[#ff0033] font-bold" : "text-zinc-600"} text-center tabular-nums`}>{kz || "—"}</div>
    <div className={`text-right tabular-nums ${muted ? "text-zinc-500" : "text-white"} ${big ? "text-[#ff0033] text-base font-bold" : ""}`}>{val}</div>
  </>
);

const StatCard = ({ icon, label, value, sub, accent, testid }) => (
  <div
    data-testid={testid}
    className={`p-5 border ${accent ? "bg-[#ff0033]/[0.06] border-[#ff0033]/30" : "bg-[#0A0A0A] border-white/[0.06]"}`}
  >
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</div>
    </div>
    <div className={`font-display font-black text-2xl ${accent ? "text-[#ff0033]" : "text-white"}`}>{value}</div>
    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{sub}</div>
  </div>
);

const PreviewList = ({ title, rows, wide, testid }) => (
  <div className={`bg-[#0A0A0A] border border-white/[0.06] ${wide ? "lg:col-span-2" : ""}`} data-testid={testid}>
    <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-bold">{title}</div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{rows.length} Einträge</div>
    </div>
    {rows.length === 0 ? (
      <div className="px-5 py-8 text-center text-xs text-zinc-500 italic">Keine Einträge in diesem Monat</div>
    ) : (
      <div className="divide-y divide-white/[0.04] max-h-72 overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="px-5 py-3 flex items-center gap-4 text-xs">
            <div className="w-16 text-zinc-500 tabular-nums">{r.date ? new Date(r.date).toLocaleDateString("de-DE") : "—"}</div>
            <div className="flex-1 min-w-0 truncate text-white font-medium">{r.left}</div>
            <div className="flex-1 min-w-0 truncate text-zinc-400">{r.middle}</div>
            <div className="text-right font-bold text-white tabular-nums">{r.right}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);
